import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Info,
  MessageSquare,
  Send,
  ShoppingBag,
  StickyNote,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ALLOWED_TRANSITIONS,
  disputesService,
  type DisputePriority,
  type DisputeStatus,
  type DisputeType,
} from '@services/disputes.service';
import type { ApiError } from '@apptypes/index';
import { cn, formatCurrency } from '@lib/utils';

// ── Label / token maps ─────────────────────────────────────────────────────
const statusChip: Record<DisputeStatus, string> = {
  open: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  under_review: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  awaiting_merchant_response: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  resolved: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  closed: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const statusLabel: Record<DisputeStatus, string> = {
  open: 'Open',
  under_review: 'Under Review',
  awaiting_merchant_response: 'Awaiting Merchant',
  resolved: 'Resolved',
  closed: 'Closed',
};

const typeLabel: Record<DisputeType, string> = {
  product_not_received: 'Product Not Received',
  damaged_product: 'Damaged Product',
  wrong_product: 'Wrong Product',
  missing_items: 'Missing Items',
  poor_quality: 'Poor Quality',
  other: 'Other',
};

const resolutionLabel: Record<string, string> = {
  full_refund: 'Full Refund',
  partial_refund: 'Partial Refund',
  replacement: 'Replacement',
  store_credit: 'Store Credit',
};

const senderTypeLabel: Record<string, string> = {
  customer: 'Customer',
  merchant: 'Merchant',
  internal_staff: 'Chekit Staff',
};

function errMessage(err: unknown): string {
  return (err as ApiError)?.message || 'Something went wrong';
}

export const DisputeDetailPage = () => {
  const { disputeId } = useParams<{ disputeId: string }>();
  const queryClient = useQueryClient();

  const [messageText, setMessageText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const { data: dispute, isLoading, error } = useQuery({
    queryKey: ['dispute', disputeId],
    queryFn: () => disputesService.get(disputeId!),
    enabled: !!disputeId,
  });

  const { data: messages } = useQuery({
    queryKey: ['dispute-messages', disputeId],
    queryFn: () => disputesService.getMessages(disputeId!),
    enabled: !!disputeId,
    refetchInterval: 15_000, // keep the thread fresh while staff review
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['dispute', disputeId] });
    queryClient.invalidateQueries({ queryKey: ['dispute-messages', disputeId] });
    queryClient.invalidateQueries({ queryKey: ['disputes'] });
  };

  const sendMessageMutation = useMutation({
    mutationFn: () =>
      disputesService.postMessage(disputeId!, {
        message: messageText.trim(),
        isInternalNote,
      }),
    onSuccess: () => {
      setMessageText('');
      setIsInternalNote(false);
      invalidateAll();
    },
    onError: (err) => toast.error(errMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { status?: DisputeStatus; priority?: DisputePriority }) =>
      disputesService.update(disputeId!, payload),
    onSuccess: (_data, payload) => {
      if (payload.status) toast.success(`Status updated to "${statusLabel[payload.status]}"`);
      if (payload.priority) toast.success('Priority updated');
      invalidateAll();
    },
    onError: (err) => toast.error(errMessage(err)),
  });

  const resolveMutation = useMutation({
    mutationFn: () =>
      disputesService.resolve(disputeId!, {
        resolutionNotes: resolutionNotes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Dispute resolved. Both parties have been notified.');
      setShowResolveModal(false);
      setResolutionNotes('');
      invalidateAll();
    },
    onError: (err) => toast.error(errMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
        Loading dispute...
      </div>
    );
  }

  if (error || !dispute) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-[14px] text-[var(--state-danger)] mb-4">
            {error ? 'Failed to load dispute.' : 'Dispute not found.'}
          </p>
          <Link
            to="/disputes"
            className="text-[14px] font-semibold text-[var(--text-accent)] transition-colors"
          >
            Back to Disputes
          </Link>
        </div>
      </div>
    );
  }

  const nextStatuses = ALLOWED_TRANSITIONS[dispute.status] ?? [];
  const transitionButtons = nextStatuses.filter((s) => s !== 'resolved');
  const canResolve = nextStatuses.includes('resolved');
  const deadlineMs = dispute.deadline ? new Date(dispute.deadline).getTime() - Date.now() : null;
  const deadlineActive = deadlineMs !== null && dispute.status !== 'resolved' && dispute.status !== 'closed';
  const evidenceUrls = dispute.evidenceUrls ?? [];

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-6">
      {/* Back link */}
      <Link
        to="/disputes"
        className="inline-flex items-center gap-2 text-[14px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to Disputes
      </Link>

      {/* Header card */}
      <div className="rounded-3xl bg-white shadow-soft p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-[28px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              {dispute.subject}
            </h1>
            <div className="text-[14px] text-[var(--text-muted)] mt-1">
              Raised {formatDistanceToNow(new Date(dispute.raisedAt), { addSuffix: true })}
              {' · '}
              {format(new Date(dispute.raisedAt), 'MMM dd, yyyy HH:mm')}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full text-[12px] font-semibold px-3 py-1',
                  statusChip[dispute.status],
                )}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {statusLabel[dispute.status]}
              </span>
              <span className="inline-flex items-center rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[12px] font-semibold px-3 py-1">
                {typeLabel[dispute.disputeType] ?? dispute.disputeType}
              </span>
              {deadlineActive && (
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 text-[13px] font-semibold',
                    deadlineMs! < 0
                      ? 'text-[var(--state-danger)]'
                      : deadlineMs! < 12 * 60 * 60 * 1000
                        ? 'text-[var(--state-warning)]'
                        : 'text-[var(--state-success)]',
                  )}
                >
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {deadlineMs! < 0
                    ? 'Deadline overdue'
                    : `Deadline ${formatDistanceToNow(new Date(dispute.deadline!), { addSuffix: true })}`}
                </span>
              )}
            </div>
          </div>

          {/* Priority selector + resolve */}
          <div className="flex items-center gap-3 shrink-0">
            <label className="text-[13px] font-semibold text-[var(--text-muted)]">Priority</label>
            <select
              value={dispute.priority}
              disabled={updateMutation.isPending}
              onChange={(e) => updateMutation.mutate({ priority: e.target.value as DisputePriority })}
              className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors cursor-pointer"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            {canResolve && (
              <button
                onClick={() => setShowResolveModal(true)}
                className="rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 flex items-center gap-2 hover:brightness-110 active:brightness-95"
              >
                <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                Resolve
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer's case */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)] flex items-center gap-2 pb-4 mb-4 border-b border-[var(--border-subtle)]">
              <FileText className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              Customer&apos;s Case
            </h2>
            <div className="bg-[var(--bg-base)] rounded-2xl p-4 text-[14px] text-[var(--text-secondary)] leading-[1.6] whitespace-pre-wrap">
              {dispute.description}
            </div>
            {evidenceUrls.length > 0 && (
              <div className="mt-4">
                <div className="text-[13px] font-semibold text-[var(--text-primary)] mb-3">
                  Evidence ({evidenceUrls.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {evidenceUrls.map((url, i) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all"
                    >
                      <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Evidence {i + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resolution notes (once resolved) */}
          {dispute.resolutionNotes && (
            <div className="rounded-3xl bg-[var(--state-success-bg)] border border-[var(--border-subtle)] p-6">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-[var(--state-success)]" strokeWidth={1.5} />
                Resolution
              </h2>
              <p className="text-[14px] text-[var(--text-secondary)] leading-[1.6] whitespace-pre-wrap">
                {dispute.resolutionNotes}
              </p>
              {dispute.resolvedAt && (
                <p className="text-[12px] text-[var(--text-muted)] mt-3">
                  Resolved {format(new Date(dispute.resolvedAt), 'MMM dd, yyyy HH:mm')}
                </p>
              )}
            </div>
          )}

          {/* Message thread */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)] flex items-center gap-2 pb-4 mb-4 border-b border-[var(--border-subtle)]">
              <MessageSquare className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              Messages
            </h2>

            <div className="space-y-5 mb-6">
              {(messages ?? []).map((msg) => {
                const isStaff = msg.senderType === 'internal_staff';
                return (
                  <div key={msg.id} className={cn('flex gap-3', isStaff ? 'flex-row-reverse' : 'flex-row')}>
                    <div className="w-9 h-9 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center text-[var(--text-accent)] font-semibold text-[13px] shrink-0">
                      {(msg.senderName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className={cn('flex-1 min-w-0', isStaff && 'flex flex-col items-end')}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-semibold text-[var(--text-primary)]">
                          {msg.senderName}
                        </span>
                        <span className="text-[11px] text-[var(--text-muted)]">
                          {senderTypeLabel[msg.senderType] ?? msg.senderType}
                        </span>
                        {msg.isInternalNote && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5">
                            <StickyNote className="w-3 h-3" strokeWidth={1.5} />
                            Internal note
                          </span>
                        )}
                      </div>
                      <div
                        className={cn(
                          'rounded-2xl p-4 text-[14px] leading-[1.6] max-w-[85%] whitespace-pre-wrap',
                          msg.isInternalNote
                            ? 'bg-amber-50 border border-amber-200 text-[var(--text-primary)]'
                            : isStaff
                              ? 'bg-[var(--brand-forest)] text-white'
                              : 'bg-[var(--bg-base)] text-[var(--text-primary)]',
                        )}
                      >
                        {msg.message}
                        {(msg.attachments ?? []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.attachments!.map((url, i) => (
                              <a
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[12px] font-semibold underline"
                              >
                                <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                Attachment {i + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-[11px] text-[var(--text-muted)] mt-1">
                        {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                );
              })}

              {messages && messages.length === 0 && (
                <div className="py-8 text-center text-[14px] text-[var(--text-muted)]">
                  No messages yet.
                </div>
              )}
            </div>

            {/* Composer */}
            <div
              className={cn(
                'border rounded-2xl p-4 transition-colors',
                isInternalNote ? 'border-amber-300 bg-amber-50/50' : 'border-[var(--border-subtle)]',
              )}
            >
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder={
                  isInternalNote
                    ? 'Add an internal note (visible to Chekit staff only)...'
                    : 'Send a message to the parties...'
                }
                className="w-full min-h-[90px] rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-y"
              />
              <div className="flex items-center justify-between mt-3">
                <label className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text-secondary)] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isInternalNote}
                    onChange={(e) => setIsInternalNote(e.target.checked)}
                    className="w-4 h-4 accent-amber-600"
                  />
                  <StickyNote className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                  Internal note
                </label>
                <button
                  onClick={() => sendMessageMutation.mutate()}
                  disabled={!messageText.trim() || sendMessageMutation.isPending}
                  className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 flex items-center gap-2 hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  {sendMessageMutation.isPending
                    ? 'Sending...'
                    : isInternalNote
                      ? 'Add Note'
                      : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Dispute details */}
          <div className="rounded-3xl bg-white shadow-soft p-6">
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)] flex items-center gap-2 pb-4 mb-2 border-b border-[var(--border-subtle)]">
              <Info className="w-5 h-5 text-[var(--text-accent)]" strokeWidth={1.5} />
              Details
            </h2>
            <div>
              <SidebarRow label="Disputed Amount">
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                  {formatCurrency(dispute.disputedAmount)}
                </span>
              </SidebarRow>
              <SidebarRow label="Requested">
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                  {resolutionLabel[dispute.resolutionRequested] ?? dispute.resolutionRequested}
                </span>
              </SidebarRow>
              <SidebarRow label="Order">
                <Link
                  to={`/orders/${dispute.orderId}`}
                  className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--text-accent)] hover:underline"
                >
                  <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {dispute.orderId.slice(0, 8)}
                </Link>
              </SidebarRow>
              {dispute.basketId && (
                <SidebarRow label="Basket">
                  <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                    {dispute.basketId.slice(0, 8)}
                  </span>
                </SidebarRow>
              )}
              <SidebarRow label="Business">
                <Link
                  to={`/businesses/${dispute.businessId}`}
                  className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--text-accent)] hover:underline"
                >
                  <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {dispute.businessId.slice(0, 8)}
                </Link>
              </SidebarRow>
              <SidebarRow label="Deadline">
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                  {dispute.deadline ? format(new Date(dispute.deadline), 'MMM dd, yyyy HH:mm') : '—'}
                </span>
              </SidebarRow>
              {dispute.closedAt && (
                <SidebarRow label="Closed">
                  <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                    {format(new Date(dispute.closedAt), 'MMM dd, yyyy HH:mm')}
                  </span>
                </SidebarRow>
              )}
            </div>
          </div>

          {/* Status transitions */}
          {(transitionButtons.length > 0 || canResolve) && (
            <div className="rounded-3xl bg-white shadow-soft p-6">
              <h2 className="text-[17px] font-semibold text-[var(--text-primary)] pb-4 mb-4 border-b border-[var(--border-subtle)]">
                Move Dispute Forward
              </h2>
              <div className="space-y-2">
                {transitionButtons.map((status) => (
                  <button
                    key={status}
                    onClick={() => updateMutation.mutate({ status })}
                    disabled={updateMutation.isPending}
                    className="w-full rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Mark as {statusLabel[status]}
                  </button>
                ))}
                {canResolve && (
                  <button
                    onClick={() => setShowResolveModal(true)}
                    className="w-full rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 active:brightness-95"
                  >
                    Resolve Dispute
                  </button>
                )}
              </div>
              {nextStatuses.includes('awaiting_merchant_response') && (
                <p className="text-[12px] text-[var(--text-muted)] mt-3 leading-[1.5]">
                  Marking as Awaiting Merchant sets a 48-hour response deadline and notifies the
                  merchant.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resolve modal */}
      {showResolveModal && (
        <div
          className="fixed inset-0 bg-[rgba(15,61,30,0.45)] z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowResolveModal(false);
          }}
        >
          <div className="rounded-3xl bg-white p-8 max-w-lg w-full shadow-lift">
            <div className="mb-5">
              <h2 className="text-[22px] font-semibold text-[var(--text-primary)]">Resolve Dispute</h2>
              <p className="text-[15px] text-[var(--text-secondary)] mt-1">
                Marks the dispute resolved and notifies both parties.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                resolveMutation.mutate();
              }}
            >
              <div className="mb-5">
                <label className="block text-[13px] font-semibold text-[var(--text-primary)] mb-2">
                  Resolution Notes
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain the outcome and reasoning behind this resolution..."
                  className="w-full min-h-[110px] rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-y"
                />
              </div>

              {/* Refund flow hint */}
              <div className="mb-6 rounded-2xl bg-[var(--state-warning-bg)] p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-[var(--state-warning)] shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-[13px] text-[var(--text-secondary)] leading-[1.5]">
                  Resolving does <span className="font-semibold">not</span> move any money. If the
                  resolution includes a refund, process it separately through the admin
                  payment-reversal flow after resolving.
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowResolveModal(false)}
                  className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resolveMutation.isPending}
                  className="rounded-full bg-[var(--gradient-to)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 flex items-center gap-2 hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                  {resolveMutation.isPending ? 'Resolving...' : 'Confirm Resolution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Small sidebar key/value row
const SidebarRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-[var(--border-subtle)] last:border-0">
    <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
      {label}
    </span>
    {children}
  </div>
);
